import { useEffect, useCallback, useRef } from "react";
import { useLocation } from "wouter";

interface UseScrollToHashOptions {
  offset?: number;
  behavior?: ScrollBehavior;
  timeout?: number;
}

export function useScrollToHash(options: UseScrollToHashOptions = {}) {
  const { 
    offset = 80, 
    behavior = "smooth",
    timeout = 10000
  } = options;
  
  const [location] = useLocation();
  const lastScrolledHash = useRef<string>("");
  const observerRef = useRef<MutationObserver | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const scrollToElement = useCallback((hash: string, scrollBehavior: ScrollBehavior = behavior) => {
    if (!hash) return false;
    
    const id = hash.replace("#", "");
    const element = document.getElementById(id);
    
    if (element) {
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: scrollBehavior
      });
      return true;
    }
    return false;
  }, [offset, behavior]);

  const cleanupObserver = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const scrollToHashWithLazyLoad = useCallback((hash: string) => {
    const id = hash.replace("#", "");
    
    if (scrollToElement(hash)) {
      lastScrolledHash.current = hash;
      return;
    }
    
    cleanupObserver();
    
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: "auto"
    });
    
    let scrollAttempts = 0;
    const maxScrollAttempts = 5;
    
    const progressiveScroll = () => {
      if (scrollToElement(hash)) {
        lastScrolledHash.current = hash;
        cleanupObserver();
        return;
      }
      
      scrollAttempts++;
      if (scrollAttempts < maxScrollAttempts) {
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: "auto"
        });
        setTimeout(progressiveScroll, 200);
      }
    };
    
    setTimeout(progressiveScroll, 100);
    
    observerRef.current = new MutationObserver((mutations) => {
      const element = document.getElementById(id);
      if (element) {
        cleanupObserver();
        
        setTimeout(() => {
          scrollToElement(hash);
          lastScrolledHash.current = hash;
        }, 50);
      }
    });
    
    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    timeoutRef.current = setTimeout(() => {
      cleanupObserver();
      scrollToElement(hash);
    }, timeout);
    
  }, [scrollToElement, cleanupObserver, timeout]);

  const scrollToHash = useCallback(() => {
    const hash = window.location.hash;
    if (hash && hash !== lastScrolledHash.current) {
      scrollToHashWithLazyLoad(hash);
    }
  }, [scrollToHashWithLazyLoad]);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      lastScrolledHash.current = "";
      setTimeout(() => scrollToHash(), 50);
    }
  }, [location, scrollToHash]);

  useEffect(() => {
    const initialHash = window.location.hash;
    if (initialHash) {
      setTimeout(() => {
        scrollToHashWithLazyLoad(initialHash);
      }, 100);
    }
    
    const handleHashChange = () => {
      scrollToHash();
    };
    
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href*="#"]') || target.closest('button[data-hash]');
      
      if (anchor) {
        const href = anchor.getAttribute('href') || anchor.getAttribute('data-hash');
        if (href) {
          const hashIndex = href.indexOf('#');
          if (hashIndex !== -1) {
            const hash = href.substring(hashIndex);
            const path = href.substring(0, hashIndex);
            
            if (!path || path === window.location.pathname) {
              e.preventDefault();
              e.stopPropagation();
              
              const currentUrl = new URL(window.location.href);
              currentUrl.hash = hash;
              
              window.history.pushState(null, "", currentUrl.pathname + currentUrl.search + hash);
              scrollToHashWithLazyLoad(hash);
            }
          }
        }
      }
    };
    
    window.addEventListener("hashchange", handleHashChange);
    document.addEventListener("click", handleAnchorClick, true);
    
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
      document.removeEventListener("click", handleAnchorClick, true);
      cleanupObserver();
    };
  }, [scrollToHash, scrollToHashWithLazyLoad, scrollToElement, cleanupObserver]);

  const scrollTo = useCallback((hash: string) => {
    if (!hash.startsWith("#")) {
      hash = "#" + hash;
    }
    
    window.history.pushState(null, "", window.location.pathname + hash);
    scrollToHashWithLazyLoad(hash);
  }, [scrollToHashWithLazyLoad]);

  return { scrollTo, scrollToHash };
}
